import React, { useState, Fragment, useEffect } from 'react';
import { SelectInput as RaSelectInput, Button, InputHelperText } from 'react-admin';
import { useForm } from 'react-final-form';
import { useInput } from 'ra-core';
import {
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  FormHelperText,
} from '@material-ui/core';
import lodashGet from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import cx from 'classnames';

import { Longdash } from '~/utils';
import { SelectArrayInput } from 'react-admin';

const columns = 3;
const rowHeight = 36;

const useStyles = makeStyles(() => ({
  fullWidth: {
    width: '100%',
  },
  selectInput: {
    marginBottom: 3,
    minWidth: 156,
  },
  menuItem: {
    width: '100%',
    height: rowHeight,
  },
  dialog: {
    maxWidth: 'auto',
  },
  columnLayout: {
    display: 'block',
    columnCount: columns,
  },
  pagination: {
    '& > div': {
      backgroundColor: 'transparent',
      borderTop: 'none',
    },
  },
}));

type ChoiceBase = {
  id: string;
  [x: string]: any;
};

interface Props<Choice> {
  allowEmpty?: boolean;
  choices?: Choice[];
  className?: string;
  classes?: any; // change to more specific type
  emptyText?: string;
  fullWidth?: boolean;
  input?: any;
  isRequired?: boolean;
  label?: string;
  meta?: any;
  optionText: string | ((x: Choice) => string);
  pagination?: {
    page: number;
    perPage: number;
  };
  resource?: string;
  modalAfterCount?: number;
  source?: string;
  children?: (item: Choice, index: number, arr: Choice[]) => any;
  onChange?: (source: string, value: string) => void;
  multiselect?: boolean;
  [x: string]: any;
}

function ModalOrSelectInput<Choice extends ChoiceBase>(props: Props<Choice>) {
  const {
    choices = [],
    source = '',
    allowEmpty,
    emptyText = Longdash,
    label,
    optionText,
    children,
    modalAfterCount = 10,
    validate,
    fullWidth,
    onChange,
    multiselect,
    ...rest
  } = props;

  const {
    input,
    isRequired,
    meta: { error, touched },
  } = useInput({ source, validate, ...rest });

  const [rows, setRows] = useState<Choice[]>([]);
  useEffect(() => {
    setRows(allowEmpty ? [{ id: emptyText } as any, ...choices] : choices);
  }, [choices, allowEmpty, emptyText]);

  // State for selected items which can be a string or array of strings depending if it is multiselect or not.
  const [selected, setSelected] = useState<string | string[]>(
    multiselect ? [...input?.value] || [] : input?.value || ''
  );
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(24);

  const form = useForm();
  const classes = useStyles();

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    if (!event.target) return;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleSelect = (itemId: string) => {
    if (multiselect) {
      // Find index of item.
      const selectedIndex = selected.indexOf(itemId);
      // Create new array.
      let newSelected: string[] = [];
      if (selectedIndex === -1) {
        // If not found then adds old one with new item to the end.
        newSelected = newSelected.concat(selected, itemId);
      } else if (selectedIndex === 0) {
        // If first then the item is being removed from the beginning.
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        // If last then the item is being removed from the end.
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        // If found in the middle then removed from the middle.
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      // Set state to a new array.
      setSelected(newSelected);
    } else {
      setSelected(itemId);
    }
  };

  const isSelected = (item: string) =>
    multiselect ? selected.indexOf(item) !== -1 : selected === item;

  const selectedValue = rows.find((item) => item.id === input.value);

  const selectedValues = rows.filter((item) => selected.includes(item.id));

  return rows.length > modalAfterCount ? (
    <Fragment>
      <FormControl
        className={cx(classes.selectInput, { [classes.fullWidth]: fullWidth })}
        onClick={handleOpen}
      >
        <InputLabel required={isRequired} variant='filled'>
          {label}
        </InputLabel>
        <Select
          multiple={multiselect}
          value={selected}
          label={label}
          MenuProps={{ open: false }}
          open={false}
          error={!!(touched && error)}
        >
          {multiselect ? (
            selectedValues.map((selectedValue) => (
              <MenuItem key={selectedValue.id} value={selectedValue.id}>
                {typeof optionText === 'string'
                  ? lodashGet(selectedValue, optionText)
                  : optionText(selectedValue)}
              </MenuItem>
            ))
          ) : (
            <MenuItem value={input.value as any}>
              {!selectedValue
                ? ''
                : typeof optionText === 'string'
                ? lodashGet(selectedValue, optionText)
                : optionText(selectedValue)}
            </MenuItem>
          )}
        </Select>
        <FormHelperText error={!!(touched && error)}>
          {touched && error ? (
            <InputHelperText touched={touched} error={error} />
          ) : (
            <span>&#8203;</span>
          )}
        </FormHelperText>
      </FormControl>

      <Dialog onClose={handleClose} aria-labelledby='simple-dialog-title' open={open}>
        <DialogTitle id='simple-dialog-title'>{label}</DialogTitle>
        <DialogContent>
          <div
            className={classes.columnLayout}
            style={{ minHeight: (rowsPerPage / columns) * rowHeight }}
          >
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .filter(Boolean)
              .map((item, index, arr) => {
                if (item.id === emptyText) {
                  return (
                    <MenuItem
                      component='button'
                      selected={isSelected('')}
                      onClick={() => handleToggleSelect('')}
                      value={''}
                      className={classes.menuItem}
                    >
                      {emptyText}
                    </MenuItem>
                  );
                }
                return (
                  <MenuItem
                    component='button'
                    selected={isSelected(item.id)}
                    onClick={() => handleToggleSelect(item.id)}
                    key={index}
                    value={item.id}
                    className={classes.menuItem}
                  >
                    {typeof children === 'function'
                      ? children(item, index, arr)
                      : typeof optionText === 'string'
                      ? lodashGet(item, optionText) || ''
                      : optionText(item)}
                  </MenuItem>
                );
              })}
          </div>
          <TablePagination
            rowsPerPageOptions={[24, 48]}
            component='div'
            className={classes.pagination}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </DialogContent>
        <DialogActions>
          <Button label={'ra.action.cancel'} onClick={handleClose} />
          <Button
            label={'ok'}
            onClick={() => {
              form.change(source, selected);
              typeof onChange === 'function' &&
                typeof selected === 'string' &&
                onChange(source, selected);
              handleClose();
            }}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  ) : multiselect ? (
    <SelectArrayInput {...props} />
  ) : (
    <RaSelectInput {...props} />
  );
}

export default ModalOrSelectInput;
